import React, { useState, useEffect } from 'react';
import { useAppSelector, useAppDispatch } from '../../store/hooks';
import { fetchDocument, getDirectDocumentUrl } from '../../api/documentApi';
import { closeDocument } from '../../store/document/slice';
import PDFViewer from './components/PDFViewer';
import HTMLViewer from './components/HTMLViewer';
import { FileType, Document } from '../../store/document/types';
import { Button } from '../UI';
import styles from './DocumentViewer.module.scss';

const DocumentViewer = () => {
  const dispatch = useAppDispatch();
  const [directUrl, setDirectUrl] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [fileType, setFileType] = useState<FileType | null>(null);
  const [documentTitle, setDocumentTitle] = useState<string>('');
  const [doc, setDoc] = useState<Document | null>(null);
  const { documentId, pageId, textToHighlight } = useAppSelector((state) => state.document);
  const isMobile = useAppSelector((state) => state.app.isMobile);

  // Preload the PDF worker script to improve loading time for PDFs
  useEffect(() => {
    // Preload the PDF.js worker script
    const preloadLink = document.createElement('link');
    preloadLink.rel = 'preload';
    preloadLink.href = 'https://unpkg.com/pdfjs-dist@3.11.174/build/pdf.worker.min.js';
    preloadLink.as = 'script';
    document.head.appendChild(preloadLink);

    return () => {
      document.head.removeChild(preloadLink);
    };
  }, []);

  // Fetch document information if documentId is provided
  useEffect(() => {
    if (documentId) {
      const getDocumentInfo = async () => {
        setIsLoading(true);
        try {
          const document = await fetchDocument(documentId);
          setDoc(document);
          setFileType(document.file_type);
          setDocumentTitle(document.name || 'Document');

          // Get a direct URL for the document
          const directDocUrl = await getDirectDocumentUrl(document);
          setDirectUrl(directDocUrl);
        } catch (err) {
          console.error('Error fetching document info:', err);
          // Keep using the original URL if there's an error
        } finally {
          setIsLoading(false);
        }
      };

      getDocumentInfo();
    } else {
      setDirectUrl(null);
      setFileType(null);
      setDoc(null);
    }
  }, [documentId]);

  const handleClose = () => {
    dispatch(closeDocument({}));
  };

  // Return null when there's no document, this will trigger the :empty CSS selector
  if (!documentId) {
    return null;
  }

  if (isLoading) {
    return (
      <div className={styles.documentContainer}>
        <div className={styles.header}>
          {isMobile && <div className={styles.documentTitle}>Loading...</div>}
          <Button variant="ghost" onClick={handleClose} aria-label="Close document">
            ✕
          </Button>
        </div>
        <div className={styles.loadingContainer}>
          <div className={styles.loadingSpinner} />
          <div className={styles.loadingText}>Loading document...</div>
        </div>
      </div>
    );
  }

  if (!directUrl || !fileType) {
    return null;
  }

  const hasEmbeddings =
    !!doc &&
    doc.embedding_count !== null &&
    doc.embedding_count !== undefined &&
    doc.embedding_count > 0;

  return (
    <div className={styles.documentContainer}>
      <div className={styles.header}>
        <div className={styles.documentTitle}>{documentTitle}</div>
        <Button variant="ghost" onClick={handleClose} aria-label="Close document">
          ✕
        </Button>
      </div>
      <div className={styles.viewerContainer}>
        {fileType === FileType.PDF ? (
          <PDFViewer
            directUrl={directUrl}
            pageNumber={pageId ? parseInt(pageId) : null}
            textToHighlight={textToHighlight}
          />
        ) : (
          <HTMLViewer
            directUrl={directUrl}
            hasEmbeddings={hasEmbeddings}
            documentId={documentId}
            pageId={pageId}
            textToHighlight={textToHighlight}
          />
        )}
      </div>
    </div>
  );
};

export default DocumentViewer;
